import { useEffect, useState } from 'react';
import { RunButton, InfoCard, NoResult } from './common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';

const decisionStyle = (d = '') => {
  if (d.startsWith('SOW')) return 'bg-emerald-100 text-emerald-800';
  if (d.startsWith('HOLD') && d.includes('CONTINGENCY')) return 'bg-amber-100 text-amber-800';
  if (d.startsWith('HOLD')) return 'bg-orange-100 text-orange-800';
  return 'bg-slate-100 text-slate-700';
};

const Sowing = ({ farm, year, season, selectedCrop, crops }) => {
  const [cal, setCal] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [isat, setIsat] = useState(null);
  const [isatLoading, setIsatLoading] = useState(false);
  const [isatSeason, setIsatSeason] = useState('Kharif');

  useEffect(() => { setIsatSeason(season === 'Rabi' ? 'Rabi' : 'Kharif'); }, [season]); // eslint-disable-line react-hooks/exhaustive-deps -- setIsatSeason is a stable setter

  const runCal = async () => {
    setCalLoading(true);
    try { setCal(await api.sowingCalendar({ crop_key: selectedCrop, lat: farm.lat, lon: farm.lon })); }
    catch (e) { toast.error('Sowing calendar failed'); }
    finally { setCalLoading(false); }
  };
  const runIsat = async () => {
    setIsatLoading(true);
    try { setIsat(await api.sowingIsat({ lat: farm.lat, lon: farm.lon, season: isatSeason })); }
    catch (e) { toast.error('Weather-based sowing advisory failed'); }
    finally { setIsatLoading(false); }
  };

  const cropLabel = crops.find(c => c.key === selectedCrop)?.label || selectedCrop;

  return (
    <>
      <InfoCard title="Weather-based sowing advisory (soil moisture + rainfall + 15-day forecast)"
                subtitle="Rule-based go/no-go engine that evaluates every crop × season in the PJTSAU workbook against soil-moisture, past-5-day rainfall, temperature bands and the 15-day rainfall outlook for this location.">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <label className="text-xs text-icrisat-earth block mb-1">Season</label>
            <Select value={isatSeason} onValueChange={setIsatSeason}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Kharif">Kharif</SelectItem>
                <SelectItem value="Rabi">Rabi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <RunButton testId={APP.actions.runSowingIsat} onClick={runIsat} loading={isatLoading}>
            Run weather-based advisory
          </RunButton>
          <span className="text-xs text-muted-foreground">Deterministic per lat/lon/date · uses synthesized EO features until live GEE is enabled</span>
        </div>

        {!isat && <div className="mt-4"><NoResult /></div>}
        {isat && (
          <div className="mt-5 space-y-4 animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Surface SMAP', isat.eo_features.smap_surface, 'm³/m³'],
                ['Root-zone SMAP', isat.eo_features.smap_rootzone, ''],
                ['Rain (past 5d)', isat.eo_features.rain_recent_mm, 'mm'],
                ['Tmax/Tmin', `${isat.eo_features.tmax_c}/${isat.eo_features.tmin_c}`, '°C'],
                ['Forecast rain (15d)', isat.eo_features.forecast_rain_mm, 'mm'],
                ['Max dry spell', isat.eo_features.max_dry_spell_days, 'days'],
              ].map(([l, v, u]) => (
                <div key={l} className="rounded-lg bg-icrisat-green-soft/60 border border-icrisat-green/20 p-2">
                  <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
                  <div className="font-display text-icrisat-green">{v} <span className="text-xs">{u}</span></div>
                </div>
              ))}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crop</TableHead>
                  <TableHead>Season</TableHead>
                  <TableHead>Decision</TableHead>
                  <TableHead className="hidden md:table-cell">Adjusted window</TableHead>
                  <TableHead className="hidden lg:table-cell">Risk</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isat.advisories.map((a) => (
                  <TableRow key={`${a.crop}-${a.season}`}>
                    <TableCell className="font-medium">{a.crop}</TableCell>
                    <TableCell>{a.season}</TableCell>
                    <TableCell><Badge className={decisionStyle(a.decision)}>{a.decision}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-xs">{a.adjusted_window}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">{a.risk_flag}</TableCell>
                    <TableCell className="text-xs max-w-[380px]">{a.action}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </InfoCard>

      <InfoCard title={`Standard sowing window · ${cropLabel}`}
                subtitle="Typical PJTSAU calendar window + live monsoon-onset check for rainfed sowing.">
        <RunButton testId={APP.actions.runSowingCalendar} onClick={runCal} loading={calLoading}>Check calendar &amp; rainfall</RunButton>
        {cal && (
          <div className="mt-4 space-y-2 animate-fade-up">
            <p className="text-sm"><b>Season:</b> {cal.season}</p>
            <p className="text-sm"><b>Kharif window:</b> {cal.kharif_start} → {cal.kharif_end}</p>
            {cal.rabi_note && <p className="text-sm">{cal.rabi_note}</p>}
            <p className="text-sm text-icrisat-earth">{cal.note}</p>
            {cal.in_window && <Badge className="bg-emerald-100 text-emerald-800">Today is within the Kharif window</Badge>}
            <div className="text-sm p-3 rounded-lg bg-icrisat-green-soft/70">{cal.rain_status}</div>
          </div>
        )}
      </InfoCard>
    </>
  );
};

export default Sowing;
